import React, { useEffect, useState } from "react";
import { Smartphone, X, Download } from "lucide-react";

const DISMISS_KEY = "cm:pwaInstallDismissedAt";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

function recentlyDismissed() {
  try {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!t) return false;
    return Date.now() - t < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Banner discreto que aparece quando o app pode ser instalado como PWA.
 * Em Android/Chrome: captura `beforeinstallprompt` e usa `prompt()`.
 * Em iOS: mostra instrução de "Adicionar à tela inicial" via menu compartilhar.
 */
export function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;
    function onBIP(e) {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    }
    window.addEventListener("beforeinstallprompt", onBIP);
    // iOS não emite beforeinstallprompt — mostra mesmo assim com instruções
    if (isIOS()) setVisible(true);
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  function dismiss() {
    setVisible(false);
    setShowIOSHelp(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  }

  async function install() {
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice?.outcome === "accepted") {
        setVisible(false);
      }
      return;
    }
    if (isIOS()) {
      setShowIOSHelp(true);
    }
  }

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="mb-4 flex items-start gap-3 p-3 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
        <Smartphone size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 leading-tight">
          Instale o app no celular
        </div>
        <div className="text-xs text-slate-500 mt-0.5 leading-snug">
          Acesso rápido em campo, funciona offline, abre em tela cheia.
        </div>
        {showIOSHelp && (
          <div className="text-[11px] text-slate-600 mt-2 p-2 rounded-lg bg-slate-50 border border-slate-200 leading-snug">
            Toque em <strong>Compartilhar</strong> (ícone <span aria-hidden>↥</span>) na
            barra do Safari → role e selecione <strong>"Adicionar à Tela de Início"</strong>.
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          type="button"
          data-testid="pwa-install-btn"
          onClick={install}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
        >
          <Download size={13} /> Instalar
        </button>
        <button
          type="button"
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Fechar"
          className="self-end text-[11px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
        >
          <X size={12} /> agora não
        </button>
      </div>
    </div>
  );
}
